/**
 * Redo: a dispatched case that came back from the dentist returns to the floor.
 *
 * As a NEW job order linked to the one it remakes, never by reopening the
 * original. The original was delivered and billed, and its stage history is the
 * record of that work; reopening it would make one case read as though it took
 * four months. The redo takes the next ordinary number and carries `redoOf`,
 * because a suffix such as JO-2054-R1 breaks the pattern the contract, the number
 * generator and the QR values all rely on, and "Redo of JO-2054" reads better
 * than a suffix anyway.
 */

/*
 * There is no stage to choose any more. The old design asked which station the
 * case went back to; a remake booked in at intake starts at Intake like every
 * other case, and the reason says what was wrong with it.
 */

export const REDO_REASON_MAX = 400;

/**
 * The longest a job order number from before this system may be, after
 * trimming: the column the original's number is kept in. The request of
 * 6 October 2026 (second), item 1.
 */
export const REDO_LEGACY_NUMBER_MAX = 32;

/**
 * The refusals, worded once for the mock server, the intake form and the tests.
 *
 * Redo moved from the archive record to intake with the request of
 * 16 September 2026, section 7: the case physically arrives back at the lab in a
 * box, somebody at reception opens it and books it in, and that is the same
 * motion as any other case. Nothing is copied from the original, because a
 * remake is rarely identical, and a prefilled form invites somebody to accept
 * last time's values without reading them, which is exactly how the wrong shade
 * gets made twice.
 */
export const REDO_REFUSAL = {
  reasonRequired: 'Say why the case came back',
  reasonTooLong: `Keep the reason to ${REDO_REASON_MAX} characters`,
  /** Not in the archive: only a case that has left the lab can come back to it. */
  notInArchive: 'That case is not in the archive',
  notAllowed: 'You cannot mark a case as a redo.',
  /*
   * A remake of a case from before this system. The request of 6 October 2026
   * (second), item 1: there is no original to find in the archive, so the
   * person types its old job order number. That number is required, keeps to
   * the column, and may not be a case this system holds, a deleted one
   * included: such a case is found in the archive like any other.
   */
  legacyNumberRequired: 'Enter the job order number of the original case',
  legacyNumberTooLong: `Keep the job order number to ${REDO_LEGACY_NUMBER_MAX} characters`,
  legacyInSystem: 'That case is in this system. Find it in the archive instead.',
} as const;

/** The 409 for a case already sent back. It names the redo, so the reader can go to it. */
export function alreadySentBackDetail(redoneAs: string): string {
  return `That case has already been sent back as ${redoneAs}.`;
}

/**
 * What is wrong with an old job order number as typed, or null when nothing
 * is: one to thirty two characters once trimmed. The server asks two things
 * more that only it can answer: that the number is not a case of this system,
 * and that the original has not been sent back already.
 */
export function checkLegacyRedoNumber(typed: string | null | undefined): string | null {
  const number = (typed ?? '').trim();
  if (number === '') return REDO_REFUSAL.legacyNumberRequired;
  if (number.length > REDO_LEGACY_NUMBER_MAX) return REDO_REFUSAL.legacyNumberTooLong;
  return null;
}

/**
 * Whether the case a remake names is one this system holds, and so one the
 * screens may link to. An original from before the system is a number somebody
 * typed and nothing more: it is shown as plain text, never as a link.
 */
export function redoOriginalInSystem(order: {
  redoOf: string | null;
  redoOfLegacy?: boolean | null;
}): boolean {
  return order.redoOf !== null && order.redoOfLegacy !== true;
}

/**
 * The sentence the log keeps on a remake, as the server writes it. A remake of
 * a case from before this system says so, and its original has no row of its
 * own to say where it went: there is no such case here.
 */
export function redoOpenedAction(original: string, legacy: boolean): string {
  return legacy
    ? `opened as a redo of ${original}, a case from before this system`
    : `opened as a redo of ${original}`;
}
